import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  InsignConnectionRequestModel,
  InsignListResponseModel,
  InsignListRequestModel,
  InsuranceTransferRequestModel,
  InsignProcedureModel,
  InsignSignatureFieldModel,
} from '../../model';

@Injectable({
  providedIn: 'root',
})
export class InsignService {
  constructor(private http: HttpClient) {}

  public startInsignConnection(
    advisorUid: string,
    body: InsignConnectionRequestModel,
    title: string,
    anonymous: boolean,
  ): Observable<string> {
    const params: HttpParams = new HttpParams()
      .set('clientId', body.clientId)
      .set('title', title)
      .set('clientFirstName', body.clientFirstName)
      .set('clientLastName', body.clientLastName)
      .set('clientEmail', body.clientEmail)
      .set('anonymous', anonymous);

    return this.http.get(`${environment.apiEndpoint}/insign/procedures/${advisorUid}`, {
      params,
      responseType: 'text',
    });
  }

  public getInsignProceduresByClient(
    advisorUid: string,
    clientUid: string,
    request: InsignListRequestModel,
  ): Observable<InsignListResponseModel> {
    const params: HttpParams = new HttpParams()
      .set('status', request.status)
      .set('page', request.page)
      .set('perPage', request.perPage);

    return this.http.get<InsignListResponseModel>(
      `${environment.apiEndpoint}/insign/procedures/list/${advisorUid}/${clientUid}`,
      {
        params,
      },
    );
  }

  public getAllInsignProcedures(
    advisorUid: string,
    request: InsignListRequestModel,
  ): Observable<InsignListResponseModel> {
    const params: HttpParams = new HttpParams()
      .set('status', request.status)
      .set('page', request.page)
      .set('perPage', request.perPage);

    return this.http.get<InsignListResponseModel>(`${environment.apiEndpoint}/insign/procedures/list/${advisorUid}`, {
      params,
    });
  }

  public getClientByInsignSessionId(sessionId: string): Observable<string> {
    return this.http.get<string>(`${environment.apiEndpoint}/insign/procedures/clientid/${sessionId}`);
  }

  public downloadFilesInZipOfProcedure(procedureId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiEndpoint}/insign/documents/${procedureId}/zip`, {
      responseType: 'blob',
      observe: 'response', // This makes it return HttpResponse<Blob> instead of just Blob
    });
  }

  public deleteProcedure(procedureId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/insign/procedures/procedure/${procedureId}`);
  }

  public triggerDocumentDownload(sessionId: string): Observable<void> {
    return this.http.post<void>(`${environment.apiEndpoint}/insign/procedures/trigger-download/${sessionId}`, null);
  }

  public saveSignedUrl(sessionId: string): Observable<string> {
    return this.http.get<string>(`${environment.apiEndpoint}/insign/procedures/sign/${sessionId}`);
  }

  public deleteInsignDocument(procedureId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/insign/document/docs/${procedureId}`);
  }

  // actions
  public getLinKForSign(procedureId: number, clientUid: string): Observable<string> {
    return this.http.get<string>(`${environment.apiEndpoint}/insign/actions/link/${procedureId}/${clientUid}`);
  }

  public sendChatMessageAction(procedureId: number, clientUid: string): Observable<void> {
    return this.http.get<void>(`${environment.apiEndpoint}/insign/actions/chat/${procedureId}/${clientUid}`);
  }

  public sendEmailAction(procedureId: number, clientUid: string): Observable<void> {
    return this.http.get<void>(`${environment.apiEndpoint}/insign/actions/email/${procedureId}/${clientUid}`);
  }

  public sendExternalEmailAction(procedureId: number): Observable<void> {
    return this.http.get<void>(`${environment.apiEndpoint}/insign/actions/external-email/${procedureId}`);
  }

  public sendChatMessageAndSendEmailAction(procedureId: number, clientUid: string): Observable<void> {
    return this.http.get<void>(`${environment.apiEndpoint}/insign/actions/chat-and-email/${procedureId}/${clientUid}`);
  }

  public isProcedureSignedCheck(procedureId: number): Observable<boolean> {
    return this.http.get<boolean>(`${environment.apiEndpoint}/insign/procedures/ready/${procedureId}`);
  }

  public getProcedureLogs(procedureId: number): Observable<any> {
    return this.http.get<any>(`${environment.apiEndpoint}/insign/procedure-logs/procedure/${procedureId}`);
  }

  public getSignatureFields(procedureId: number): Observable<InsignSignatureFieldModel[]> {
    return this.http.get<InsignSignatureFieldModel[]>(
      `${environment.apiEndpoint}/insign/procedures/procedure/${procedureId}/signature-fields`,
    );
  }

  public addRecipient(procedureId: number, email: string): Observable<void> {
    return this.http.post<void>(`${environment.apiEndpoint}/insign/actions/recipient/${procedureId}`, { email });
  }

  public getInsuranceTransferInfo(): Observable<any> {
    return this.http.get<any>(`${environment.apiEndpoint}/insign/pdf/contract-mandate/sample`);
  }

  public postInsuranceTransferInfo(body: InsuranceTransferRequestModel): Observable<any> {
    return this.http.post<any>(`${environment.apiEndpoint}/insign/pdf/contract-mandate/generate`, body);
  }

  public getProcedureByReference(referenceId: string): Observable<InsignProcedureModel> {
    return this.http.get<any>(`${environment.apiEndpoint}/insign/procedures/by-reference/${referenceId}`);
  }
}
